import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSessionStore } from '@/stores'
import { setOrderDetail, formatErrorMessage } from '@/utils'
import { useToast } from '@/hooks/useToast'
import { inputTypeForPath } from '../utils/portalNav'
import { isSyncedToUrl, moveSessionToPath, resyncPortalSession } from '../utils/portalRecovery'
import { isPortalNavPending, queuePortalNav } from '../utils/portalNavQueue'
import { prefetchPortalRoute } from '../utils/portalQueries'
import { isInteractivePreviewMode } from '@/utils/previewMode'
import { isShopifyEmbed } from '@utils/embedConfig'
import { postMessageToParent } from '@utils/iframe'

function currentPath(): string {
  return window.location.pathname + window.location.search
}

export function useOmpNav() {
  const navigate = useNavigate()
  const toast = useToast()

  const navigateTo = useCallback(
    async (to: string, opts?: { orderId?: number | string; replace?: boolean }) => {
      if (isInteractivePreviewMode()) {
        navigate(to, { replace: opts?.replace })
        return
      }

      const sessionId = (useSessionStore.getState().session as any)?.id
      if (!sessionId || inputTypeForPath(to) === undefined) {
        navigate(to, { replace: opts?.replace })
        return
      }

      const from = currentPath()
      if (to === from) {
        return
      }
      prefetchPortalRoute(to)

      if (!isPortalNavPending() && isSyncedToUrl(to)) {
        navigate(to, { replace: opts?.replace })
        return
      }

      const settled = queuePortalNav(async isLatest => {
        try {
          if (opts?.orderId != null) {
            setOrderDetail(String(opts.orderId))
          }
          await moveSessionToPath(to, isLatest)
        } catch (err) {
          if (!isLatest() || !useSessionStore.getState().session) {
            return
          }
          toast.error(formatErrorMessage(err))
          const landedOnTarget = currentPath() === to
          if (landedOnTarget && !opts?.replace) {
            navigate(-1)
            return
          }
          const back = landedOnTarget ? from : currentPath()
          navigate(back, { replace: true })
          await resyncPortalSession(new URL(back, window.location.origin).pathname)
        }
      })
      navigate(to, { replace: opts?.replace })
      if (isShopifyEmbed()) {
        postMessageToParent({ type: 'scrollToTop' })
      }
      try {
        await settled
      } catch (err) {
        if (useSessionStore.getState().session) {
          toast.error(formatErrorMessage(err))
        }
      }
    },
    [navigate, toast]
  )

  const goBack = useCallback(
    async (fallbackTo?: string) => {
      if (fallbackTo) {
        await navigateTo(fallbackTo, { replace: true })
        return
      }
      navigate(-1)
    },
    [navigate, navigateTo]
  )

  return { navigateTo, goBack }
}
